/**
 * Canonical Shopify pipeline progress resolver.
 * Uses the shared CapturePipelineState resolver — percent is upload-only (Rule 1).
 */
import {
  resolveCapturePipelineState,
  internalStageFromHudNotch,
  PIPELINE_PHASE,
} from '../domain/capturePipeline/state';
import {
  resolveShopifyPipelineStage,
  formatShopifyPipelineHeadline,
} from './shopifyPipelineStages';
import { SUSTAINED_POLL_ERROR_THRESHOLD } from '../constants/timeouts';

export { SUSTAINED_POLL_ERROR_THRESHOLD };

const MAX_RUNPOD_REDISPATCH_COUNT = 3;

/** Backend polling begins only after the browser has finished sending bytes. */
export function shouldPollShopifyPipeline({
  captureId = null,
  isViewerReady = false,
  clientUploadInProgress = false,
  pipelineEnabled = true,
} = {}) {
  return Boolean(captureId)
    && pipelineEnabled
    && !isViewerReady
    && !clientUploadInProgress;
}

/** Keep byte progress and pipeline stage sourced from the same upload snapshot. */
export function shouldShowShopifyUploadBytes({
  clientUploadInProgress = false,
  isFailed = false,
  pipelineStage = 0,
} = {}) {
  return clientUploadInProgress && !isFailed && Number(pipelineStage) === 1;
}

/**
 * Single source of truth for Shopify pipeline failure UI state.
 */
export function resolveShopifyPipelineFailure({
  workerStillStarting = false,
  processingStatus = null,
  polledStatus = null,
  polledFailedAt = null,
  pipelineRecordMissing = false,
  pollError = false,
  consecutivePollErrors = 0,
  localFailed = false,
  uploadStalled = false,
  productState = null,
  hasCaptureMetadata = true,
  uploadBelongsToModal = true,
} = {}) {
  const sustainedPollError = pollError && consecutivePollErrors >= SUSTAINED_POLL_ERROR_THRESHOLD;
  const isCancelled = ['cancelled', 'canceled'].includes(String(processingStatus || '').toLowerCase())
    || ['cancelled', 'canceled'].includes(String(polledStatus || '').toLowerCase());
  if (isCancelled) {
    return { isFailed: false, sustainedPollError };
  }
  const firestoreFailed = processingStatus === 'failed';
  const polledFailed = polledStatus === 'failed';
  const polledFailureDetail = polledFailedAt != null && String(polledFailedAt).trim() !== '';
  const catalogueFailed = productState === 'failed' && !hasCaptureMetadata;

  // Explicit persisted failure statuses are authoritative. `workerStillStarting`
  // only suppresses incomplete/stale failure evidence from a previous attempt.
  if (firestoreFailed || polledFailed || localFailed || uploadStalled || catalogueFailed) {
    return { isFailed: uploadBelongsToModal !== false, sustainedPollError };
  }

  if (workerStillStarting) {
    return { isFailed: false, sustainedPollError };
  }

  const isFailed = uploadBelongsToModal !== false && (
    polledFailureDetail
    || (pipelineRecordMissing && (processingStatus === 'processing' || processingStatus === 'queued'))
  );

  return { isFailed, sustainedPollError };
}

/** During client byte upload only — no pipeline percent in Shopify UI. */
export function pickShopifyDisplayPercent({
  uploadContextPercent = null,
  productListPercent = null,
  useUploadContext = false,
  uploadPhase = null,
} = {}) {
  if (useUploadContext && uploadPhase === 'uploading') {
    const up = Number(uploadContextPercent);
    return Number.isFinite(up) ? Math.round(Math.max(0, Math.min(100, up))) : null;
  }
  if (productListPercent != null) {
    const pl = Number(productListPercent);
    if (Number.isFinite(pl)) return Math.round(Math.max(0, Math.min(100, pl)));
  }
  return null;
}

export function formatShopifyRetryHeadline(runpodRedispatchCount = 0) {
  const count = Number(runpodRedispatchCount);
  if (!Number.isFinite(count) || count < 1) return null;
  const attempt = Math.min(count, MAX_RUNPOD_REDISPATCH_COUNT);
  return `Retrying (attempt ${attempt} of ${MAX_RUNPOD_REDISPATCH_COUNT})…`;
}

export function resolveShopifyPipelineProgressState(input = {}) {
  const {
    captureMetadata = null,
    captureId = null,
    firestorePercent = null,
    polledPercent = null,
    polledStageIndex = null,
    polledStageTitle = null,
    uploadContextPercent = null,
    uploadContextStage = null,
    productListPercent = null,
    processingStatus = null,
    rawPollPercent = null,
    progressMessage = '',
    firestorePipelineStage = null,
    framesExtracted = null,
    uploadPhase = null,
    effectiveUploadPhase = null,
    useUploadContext = false,
    runpodJobStatus = null,
    stageTitle = '',
    isFailed = false,
    isCompleted = false,
    isViewerReady = null,
    workerStillStarting = false,
    runpodRedispatchCount = 0,
  } = input;

  const resolvedUploadPhase = effectiveUploadPhase ?? uploadPhase;

  const capture = captureMetadata || {
    id: captureId,
    processing: {
      status: processingStatus,
      stage: firestorePipelineStage,
      framesExtracted,
    },
    progress: {
      percent: firestorePercent,
      message: progressMessage,
    },
  };

  const pipelineState = resolveCapturePipelineState({
    capture: { ...capture, id: capture.id || captureId },
    poll: {
      status: processingStatus,
      runpodJobStatus,
      stageIndex: polledStageIndex ?? uploadContextStage,
      stageTitle: polledStageTitle,
      percent: polledPercent ?? rawPollPercent,
    },
    uploadCtx: {
      activeCaptureId: useUploadContext ? captureId : null,
      uploadPhase: resolvedUploadPhase,
      uploadPercent: useUploadContext ? uploadContextPercent : null,
    },
  });

  const displayPercent = pickShopifyDisplayPercent({
    uploadContextPercent,
    useUploadContext,
    uploadPhase: resolvedUploadPhase,
  }) ?? (productListPercent != null && !captureMetadata
    ? Math.round(Math.max(0, Math.min(100, Number(productListPercent))))
    : null);

  const internalStageIndex = internalStageFromHudNotch(pipelineState.status.hudNotch);
  const pipelineStageLabel = pipelineState.status.label;

  const headlineUploadPhase = (() => {
    if (useUploadContext) {
      if (resolvedUploadPhase === 'queueing' && internalStageIndex >= 2) return 'pipeline';
      return resolvedUploadPhase;
    }
    if (internalStageIndex >= 2 && !isFailed && !isCompleted) return 'pipeline';
    if (workerStillStarting && !isFailed && !isCompleted) return 'queueing';
    return null;
  })();

  const shopifyHeadlineStage = resolveShopifyPipelineStage({
    uploadPhase: headlineUploadPhase,
    progressMessage: pipelineState.status.label,
    stageTitle: stageTitle || pipelineStageLabel,
    runpodJobStatus,
    processingStatus,
    progressPercent: displayPercent,
    stageIndex: internalStageIndex,
    isFailed,
    isCompleted,
    isViewerReady,
  });

  const retryHeadline = formatShopifyRetryHeadline(runpodRedispatchCount);
  const headline = retryHeadline || formatShopifyPipelineHeadline(shopifyHeadlineStage);

  return {
    displayPercent,
    internalStageIndex,
    pipelineStageLabel,
    shopifyHeadlineStage,
    headline,
    retryHeadline,
    isQueueing: pipelineState.phase === PIPELINE_PHASE.QUEUED && internalStageIndex < 2,
    pipelineStatusLabel: pipelineState.status.label,
  };
}
